import { Fragment } from 'react';
import {
  FilterToolbarGroup,
  FilterToolbarItemId,
} from 'src/components/Filters';
import {
  EventTagInfoFilter,
  TagInfoFilter,
  useTagFilters,
} from 'src/components/MainFilterBar';
import { Stack } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ActionOutboxEntityType,
  UIReportV2SaleQuery,
} from 'src/WebApiController';

import { useTagFiltersBase } from './useTagFiltersBase';

export type MergedTagFilters = TagInfoFilter & EventTagInfoFilter;

interface UseSaleReportTagFiltersProps {
  query: UIReportV2SaleQuery;
  setQuery: (query: UIReportV2SaleQuery) => void;
}

export const useSaleReportTagFilters = ({
  query,
  setQuery,
}: UseSaleReportTagFiltersProps): FilterToolbarGroup[] => {
  const tagFilters = useTagFilters(
    query,
    setQuery,
    ActionOutboxEntityType.Sale,
    true
  );
  const listingTagFilters = useTagFiltersBase(
    query.listingTagName,
    query.listingTagValue,
    (listingTagName) => setQuery({ ...query, listingTagName }),
    (listingTagValue) => setQuery({ ...query, listingTagValue }),
    ContentId.InventoryTag,
    ActionOutboxEntityType.Listing,
    true
  );
  const purchaseTagFilters = useTagFiltersBase(
    query.purchaseOrderTagName,
    query.purchaseOrderTagValue,
    (purchaseOrderTagName) => setQuery({ ...query, purchaseOrderTagName }),
    (purchaseOrderTagValue) => setQuery({ ...query, purchaseOrderTagValue }),
    ContentId.PurchaseTag,
    ActionOutboxEntityType.Purchase,
    true
  );

  const tagsFilterGroup: FilterToolbarGroup = {
    titleContentId: ContentId.Tags,
    type: 'row',
    items: [
      // tags
      {
        filterId: 'tagName' as const,
        labelContentId: tagFilters.titleContentId!,
        filterQueryKeys: tagFilters.items
          .map((filterToolbarItem) => filterToolbarItem.filterQueryKeys)
          .flat() as FilterToolbarItemId[],
        filterItem: (
          <Stack gap="m">
            {tagFilters.items.map((filterToolbarItem) => (
              <Fragment key={filterToolbarItem.filterId}>
                {filterToolbarItem.filterItem}
              </Fragment>
            ))}
          </Stack>
        ),
      },
      {
        filterId: 'listingTagName' as const,
        labelContentId: listingTagFilters.titleContentId!,
        filterQueryKeys: listingTagFilters.items
          .map((filterToolbarItem) => filterToolbarItem.filterQueryKeys)
          .flat() as FilterToolbarItemId[],
        filterItem: (
          <Stack gap="m">
            {listingTagFilters.items.map((filterToolbarItem) => (
              <Fragment key={filterToolbarItem.filterId}>
                {filterToolbarItem.filterItem}
              </Fragment>
            ))}
          </Stack>
        ),
      },
      {
        filterId: 'purchaseOrderTagName' as const,
        labelContentId: purchaseTagFilters.titleContentId!,
        filterQueryKeys: purchaseTagFilters.items
          .map((filterToolbarItem) => filterToolbarItem.filterQueryKeys)
          .flat() as FilterToolbarItemId[],
        filterItem: (
          <Stack gap="m">
            {purchaseTagFilters.items.map((filterToolbarItem) => (
              <Fragment key={filterToolbarItem.filterId}>
                {filterToolbarItem.filterItem}
              </Fragment>
            ))}
          </Stack>
        ),
      },
    ],
  };

  return [tagsFilterGroup];
};
